/**
 * Centralized Authentication Utilities
 * Handles logout, cache clearing, and auth state management
 */

import { QueryClient } from '@tanstack/react-query';
import { clearAuthData } from '@/services/api';

/**
 * Clear all authentication data and React Query cache
 * Should be called on logout to ensure clean state
 */
export const performLogout = async (queryClient: QueryClient, navigate?: (path: string) => void) => {
  try {
    // Clear all React Query cache
    queryClient.clear();
    
    // Clear localStorage auth data
    clearAuthData();
    
    // Clear any other cached data
    localStorage.removeItem('appPrefetchCache');
    
    if (import.meta.env.DEV) {
      console.log('✅ All cache and auth data cleared');
    }
    
    // Navigate to login if navigate function provided
    if (navigate) {
      navigate('/login');
    } else {
      window.location.href = '/login';
    }
  } catch (error) {
    console.error('Error during logout:', error);
    // Force navigation even if cleanup fails
    clearAuthData();
    window.location.href = '/login';
  }
};

/**
 * Clear user-specific cache on login
 * Ensures new user doesn't see previous user's data
 */
export const clearUserCache = (queryClient: QueryClient) => {
  try {
    // Invalidate all queries to force fresh data fetch
    queryClient.invalidateQueries();
    
    // Clear any cached user-specific data
    queryClient.removeQueries({ queryKey: ['auth', 'currentUser'] });
    queryClient.removeQueries({ queryKey: ['users'] });
    queryClient.removeQueries({ queryKey: ['dashboard'] });
    
    if (import.meta.env.DEV) {
      console.log('✅ User cache cleared for new login');
    }
  } catch (error) {
    console.error('Error clearing user cache:', error);
  }
};

